/**
 * BESTANDTEILE-DIALOG — reine Hilfslogik der Oberflaeche fuer Mitarbeiter
 * (Plan docs/positionsbestandteile-plan.md, Stufe B3; Paket D).
 *
 * Was hier steht, entscheidet die Oberflaeche — WER welchen Knopf sieht, WELCHER
 * Satz unter einer Zeile steht, WELCHE Meldung nach einer Handlung kommt, und
 * die lokale Ueberschreibung des Stands fuer die Plakette im Hub. Bewusst ohne
 * React und ohne Netz: so ist jede Regel einzeln pruefbar (Test daneben), und
 * die Komponenten bleiben reine Bedienung.
 *
 * Was hier NICHT steht:
 *  - ob die Position abrechenbar ist (Gate) — das sagt allein der von der
 *    Datenbank gepflegte Wert `bestandteileOffen` (bestandteilLogic);
 *  - wer etwas darf — das prueft der Server (RPCs, RLS). Die Knopfregeln hier
 *    sind nur Hoeflichkeit: ein Knopf, den der Server ohnehin ablehnt, soll gar
 *    nicht erst dastehen;
 *  - Preise. Der Dialog zeigt nie einen Betrag (Kernregel 6).
 */

import type { BestandteilQuelle, PositionsBestandteil, Report, ServiceItem, ServiceTransaction } from '../../types';
import {
    BESTANDTEIL_QUELLE_LABEL,
    BESTANDTEIL_SYSTEM_NAME,
    bestandteilGate,
    bestandteilStand,
    formatTag,
    formatZeitpunkt,
    hatBestandteile,
    heuteBerlin,
    normalisiereBestandteile,
    pruefeDefinition,
    standKurz,
    type AusgabeLike,
    type BestandteilAenderung,
    type BestandteilEintrag,
    type BestandteilStand,
    type BestandteilZeile,
} from '../../services/bestandteile/bestandteilLogic';
import { bestandteilStandKurz } from '../../services/projectLv';
import {
    KONTINGENT_RPC_STATUS_TEXT,
    RPC_STATUS_TEXT,
    STUNDEN_RPC_STATUS_TEXT,
    type BestandteilRpcErgebnis,
    type KontingentRpcErgebnis,
    type PaketPositionFrisch,
    type StundenRpcErgebnis,
} from '../../services/bestandteile/bestandteilService';
import {
    KONTINGENT_MAX,
    formatStunden,
    parseStunden,
    stundenPlakette,
    stundenRechte,
    stundenStandAusPosition,
    stundenText,
    type StundenStand,
} from '../../services/bestandteile/stundenLogic';
import { aktiveBstBuchung, leistungAusEintraegen, type AusfuehrungsStatus } from '../../services/bestandteile/bestandteilBuchung';
import { READ_ONLY_HINWEIS } from '../../services/readOnlyNotice';
import { isNepService } from '../../services/nepPosition';

const TOLERANZ = 0.0005;

const zahl = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const gleicheKennung = (a: unknown, b: unknown): boolean =>
    String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

/**
 * Zeitwert eines Zeitstempels; NaN, wenn unlesbar. Postgres liefert je nach
 * Weg '2026-10-16T12:32:00+00:00' oder '2026-10-16 12:32:00.12+00' — die
 * zweite Form versteht nicht jede JS-Engine (dieselbe Normalisierung wie in
 * bestandteilLogic).
 */
const zeitMs = (iso: string | null | undefined): number => {
    let s = String(iso ?? '').trim();
    if (!s) return NaN;
    if (/^\d{4}-\d{2}-\d{2} \d/.test(s)) s = s.replace(' ', 'T');
    if (/T.*[+-]\d{2}$/.test(s)) s = `${s}:00`;
    return Date.parse(s);
};

/** Berliner Kalendertag eines Zeitstempels ('YYYY-MM-DD'), null wenn unlesbar. */
export const berlinTagVon = (iso: string | null | undefined): string | null => {
    const ms = zeitMs(iso);
    return Number.isFinite(ms) ? heuteBerlin(new Date(ms)) : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Rechte: schreiben oder nur ansehen
// ─────────────────────────────────────────────────────────────────────────────

export interface DialogRechte {
    /** Darf in diesem Dialog ueberhaupt etwas gebucht werden? */
    schreibbar: boolean;
    /** Warum nicht — fuer den sichtbaren Hinweis. null, wenn schreibbar. */
    grund: string | null;
}

/**
 * Nur-Ansicht hat drei Gruende, in dieser Reihenfolge:
 *  1. Das Projekt ist schreibgeschuetzt geoeffnet (Nur-Ansicht, fremde Sperre).
 *  2. Freelancer: die RPCs weisen sie ohnehin ab (RESTRICTIVE-Gate, Nr. 165).
 *  3. Tochterprojekt: die Liste zeigt das LV der MUTTER, eine Buchung ginge
 *     dorthin — die Automatik bucht dort nicht (bestandteilBuchung,
 *     'tochterprojekt'). Gebucht wird im Mutterprojekt.
 */
export const dialogRechte = (a: { isReadOnly: boolean; istFreelancer: boolean; istTochter: boolean; ohneProjekt?: boolean }): DialogRechte => {
    if (a.isReadOnly) return { schreibbar: false, grund: READ_ONLY_HINWEIS };
    // Noch nie gespeichertes Projekt: die RPCs faenden die Position nicht.
    if (a.ohneProjekt) return { schreibbar: false, grund: 'Das Projekt ist noch nicht gespeichert — Bestandteile lassen sich erst danach buchen.' };
    if (a.istFreelancer) return { schreibbar: false, grund: 'Bestandteile können Freelancer nur ansehen.' };
    if (a.istTochter) {
        return {
            schreibbar: false,
            grund: 'Tochterprojekt: die Bestandteile gehören zur Position im Mutterprojekt und werden dort gebucht.',
        };
    }
    return { schreibbar: true, grund: null };
};

export const istTochterBericht = (r: Report | null | undefined): boolean =>
    r?.metadata?.globalData?.linkedProjectConfig?.role === 'child';

// ─────────────────────────────────────────────────────────────────────────────
// Geschuetzter Schreibweg — nie in ein fremdes Projekt
// ─────────────────────────────────────────────────────────────────────────────

/** Projekt-Id eines Stands ('' ohne). */
export const projektIdVon = (r: Report | null | undefined): string =>
    String(r?.metadata?.globalData?.projectId ?? '').trim();

export type SchreibSperre = 'nicht_eingehaengt' | 'ohne_projekt' | 'anderes_projekt' | 'projekt_gewechselt';

/**
 * Darf dieser Stand jetzt geschrieben werden?
 *
 * Das Problem: zwischen Klick und Positionsbuchung liegen Netzrunden (RPC,
 * frisches Laden). Schliesst der Nutzer in der Zeit die Ansicht, springt „Zum
 * Modul" oder oeffnet ein anderes Projekt, kaeme `onUpdate` mit dem Stand des
 * ALTEN Projekts (+ BST-Zeile) an — und App.handleUpdateReport setzte ihn ueber
 * den neuen Projektstand. Geschrieben wird deshalb nur, wenn
 *  - der Hub noch eingehaengt ist,
 *  - das Projekt des Vorgangs bekannt ist (nicht leer, nicht 'temp-id'),
 *  - der zu schreibende Stand zu GENAU diesem Projekt gehoert,
 *  - und der Hub gerade dasselbe Projekt zeigt.
 * Sonst wird verworfen; eine faellige Positionsbuchung holt der Nachtlauf nach.
 */
export const pruefeSchreiben = (a: {
    eingehaengt: boolean;
    projektId: string;
    neu: Report | null | undefined;
    aktuell: Report | null | undefined;
}): SchreibSperre | null => {
    if (!a.eingehaengt) return 'nicht_eingehaengt';
    const id = String(a.projektId ?? '').trim();
    if (!id || id === 'temp-id') return 'ohne_projekt';
    if (projektIdVon(a.neu) !== id) return 'anderes_projekt';
    if (projektIdVon(a.aktuell) !== id) return 'projekt_gewechselt';
    return null;
};

export interface Schreibweg {
    /** Schreibt nur, wenn pruefeSchreiben nichts einzuwenden hat — sonst verworfen (Warnung). */
    onUpdate: (r: Report) => void;
    /** Der aktuelle Stand — wirft, wenn er nicht mehr zum Projekt des Vorgangs gehoert. */
    holeBericht: () => Report;
    /** Gehoert der aktuelle Stand noch zum Projekt des Vorgangs (und ist der Hub da)? */
    gueltig: () => boolean;
}

/**
 * Bindet einen Vorgang an SEIN Projekt (beim Start festgehalten). Die
 * Buchungsfunktionen (bestandteilBuchung) bekommen `onUpdate` und
 * `holeBericht` von hier: wirft `holeBericht`, fallen sie auf den beim Start
 * uebergebenen Stand zurueck — dessen Schreiben verwirft dann `onUpdate`.
 */
export const schuetzeSchreibweg = (a: {
    projektId: string;
    istEingehaengt: () => boolean;
    aktuell: () => Report;
    onUpdate: (r: Report) => void;
    melde?: (sperre: SchreibSperre) => void;
}): Schreibweg => {
    const melde = a.melde ?? ((sperre: SchreibSperre) =>
        console.warn(`Bestandteile: Schreiben verworfen (${sperre}) — es wird nie in ein anderes Projekt geschrieben; eine fällige Buchung holt der Nachtlauf nach.`));
    const gueltig = (): boolean => {
        const aktuell = a.aktuell();
        return pruefeSchreiben({ eingehaengt: a.istEingehaengt(), projektId: a.projektId, neu: aktuell, aktuell }) === null;
    };
    return {
        onUpdate: (r) => {
            const sperre = pruefeSchreiben({ eingehaengt: a.istEingehaengt(), projektId: a.projektId, neu: r, aktuell: a.aktuell() });
            if (sperre) {
                melde(sperre);
                return;
            }
            a.onUpdate(r);
        },
        holeBericht: () => {
            if (!gueltig()) throw new Error('Das Projekt wurde gewechselt — der Vorgang schreibt nichts mehr.');
            return a.aktuell();
        },
        gueltig,
    };
};

/** Meldung, wenn ein Vorgang wegen Projektwechsel/verlassener Ansicht nichts mehr schreibt. */
export const meldungProjektGewechselt = (wasGeschah: string): Meldung => ({
    ton: 'hinweis',
    text: `${wasGeschah} Die Ansicht wurde inzwischen verlassen oder das Projekt gewechselt — an der Position wird hier nichts mehr geändert; eine fällige Buchung holt der Nachtlauf nach.`,
});

// ─────────────────────────────────────────────────────────────────────────────
// Knoepfe je Zeile
// ─────────────────────────────────────────────────────────────────────────────

export interface ZeilenKnoepfe {
    /** „Erledigt buchen" (offene Zeile). */
    erledigen: boolean;
    /** „Entfällt" — nur Admin (E4). */
    entfaellt: boolean;
    /** Übernahme-Vorschlag aus einer vorhandenen Ausgabe. */
    uebernehmen: boolean;
    /** „Zurücknehmen" — eigener Eintrag oder Admin. */
    zuruecknehmen: boolean;
    /** Warum „Zurücknehmen" fehlt, obwohl die Person es duerfte (abgerechnet). */
    zuruecknehmenSperre: string | null;
}

export const ZURUECKNEHMEN_ABGERECHNET =
    'Die Position ist bereits abgerechnet — zurücknehmen geht erst nach dem Storno der Rechnung.';

/**
 * Welche Knoepfe eine Zeile zeigt.
 *
 * Zuruecknehmen: der EIGENE Eintrag (`erfasstVon` = angemeldete Person) oder
 * ein Admin — dieselbe Regel wie die RPC (E4). Bei einem Modul-Eintrag ist
 * `erfasstVon` die Person, die freigegeben/versendet hat. Abgerechnet sperrt
 * (die RPC antwortet sonst 'abgerechnet') — dann steht statt des Knopfes der
 * Grund da, damit niemand einen Fehler der Anwendung vermutet.
 */
export const zeilenKnoepfe = (a: {
    zeile: BestandteilZeile;
    schreibbar: boolean;
    isAdmin: boolean;
    userId: string | null;
    abgerechnet: boolean;
    vorschlag: AusgabeLike | null;
}): ZeilenKnoepfe => {
    const leer: ZeilenKnoepfe = { erledigen: false, entfaellt: false, uebernehmen: false, zuruecknehmen: false, zuruecknehmenSperre: null };
    if (!a.schreibbar || !a.zeile) return leer;
    if (a.zeile.status === 'offen') {
        return { ...leer, erledigen: true, entfaellt: a.isAdmin, uebernehmen: !!a.vorschlag };
    }
    const eintrag = a.zeile.eintrag;
    if (!eintrag) return leer;
    const eigen = !!a.userId && gleicheKennung(eintrag.erfasstVon, a.userId);
    if (!a.isAdmin && !eigen) return leer;
    if (a.abgerechnet) return { ...leer, zuruecknehmenSperre: ZURUECKNEHMEN_ABGERECHNET };
    return { ...leer, zuruecknehmen: true };
};

// ─────────────────────────────────────────────────────────────────────────────
// Texte: Stand, Status der Position, Zeilen
// ─────────────────────────────────────────────────────────────────────────────

/** „4/5 erledigt · 1 entfällt" — entfallene zaehlen im Bruch mit (standKurz). */
export const standText = (stand: BestandteilStand): string => {
    if (!stand || stand.gesamt <= 0) return 'Keine Bestandteile';
    const basis = `${standKurz(stand)} erledigt`;
    if (stand.entfallen <= 0) return basis;
    return `${basis} · ${stand.entfallen} ${stand.entfallen === 1 ? 'entfällt' : 'entfallen'}`;
};

/**
 * Der Wortlaut, wenn die Datenbank mehr offene Bestandteile zaehlt als die
 * App lesen kann (siehe `definitionUnlesbar`).
 */
export const UNLESBAR_HINWEIS =
    'Die gespeicherte Liste enthält Bestandteile, die diese App-Version nicht lesen kann — Position bleibt gesperrt. Admin: Bestandteile bearbeiten und neu speichern.';

/**
 * Paket aus lauter unlesbaren Bestandteilen (`[]`, A3), das die Datenbank als
 * vollstaendig meldet (offen 0): frei, aber weiter Paket — abrechenbar
 * hoechstens bis zur Vertragsmenge.
 */
export const UNLESBAR_FREI_HINWEIS =
    'Laut Datenbank sind alle Bestandteile erledigt — diese App-Version kann die Liste aber nicht lesen. Abrechenbar höchstens bis zur Vertragsmenge. Admin: Bestandteile bearbeiten und neu speichern.';

/**
 * Zaehlt die DATENBANK mehr offene Bestandteile, als die App aus Definition und
 * Eintraegen rechnet?
 *
 * Die Datenbank (Nr. 166) zaehlt fail closed JEDES Definitionselement mit nicht
 * leerem Key — auch eines ohne Label oder mit unbekannter Quelle, das
 * `normalisiereBestandteile` hier verwirft. Dann zeigte die Liste „4/4", das
 * Gate bliebe aber zu, und ein „Position jetzt buchen" waere eine Buchung auf
 * einen Stand, den nur die App fuer vollstaendig haelt. Unbekannter DB-Wert
 * (null) ist KEIN Befund hier — das ist das ohnehin gesperrte Gate „unbekannt".
 */
export const definitionUnlesbar = (stand: BestandteilStand, dbOffen: number | null | undefined): boolean =>
    // Auch eine Liste ganz ohne LESBARE Bestandteile (gesamt 0, A3: `[]` mit
    // offenen in der DB) ist so ein Fall — sie zaehlt offen 0, die DB mehr.
    !!stand && typeof dbOffen === 'number' && Number.isFinite(dbOffen) && dbOffen > stand.offen;

/**
 * Der DB-Wert „offen" einer frisch geladenen Position — aber nur, wenn er zur
 * SELBEN Liste gehoert, die der Dialog anzeigt. Sonst undefined (kein
 * Befund moeglich):
 *  - die Position steht in der Datenbank (noch) nicht als Paket (eben erst
 *    festgelegt, Speichern laeuft noch);
 *  - die lesbare Liste der Datenbank weicht von der lokalen ab (eben
 *    geaendert, die Datenbank kennt die neue Liste noch nicht) — ihr Wert
 *    zaehlte die alte Liste.
 * Verglichen wird die NORMALISIERTE Liste: eine Spalte mit zusaetzlichen,
 * unlesbaren Elementen gilt als dieselbe Liste — genau das soll der Befund
 * `unlesbar` dann zeigen.
 */
export const dbOffenAusFrisch = (
    service: Pick<ServiceItem, 'id' | 'bestandteile'>,
    frisch: Pick<PaketPositionFrisch, 'id' | 'bestandteile' | 'bestandteileOffen'> | null | undefined,
): number | null | undefined => {
    if (!frisch || !gleicheKennung(frisch.id, service?.id)) return undefined;
    // Lokal gar keine Liste (null: eben alle Bestandteile entfernt, Speichern
    // laeuft noch): der DB-Wert gehoert zur alten Liste. `[]` dagegen ist eine
    // Liste — die aus lauter unlesbaren Elementen (A3).
    if (!Array.isArray(service?.bestandteile)) return undefined;
    const db = JSON.stringify(normalisiereBestandteile(frisch.bestandteile) ?? null);
    const lokal = JSON.stringify(normalisiereBestandteile(service?.bestandteile) ?? null);
    return db === lokal ? frisch.bestandteileOffen : undefined;
};

/**
 * Stand und Befund aus EINER Ladung (Eintraege + frischer Positionsstand
 * zusammen). Bewertet wird `unlesbar` nur gegen einen frischen DB-Wert — nie
 * gegen den Wert im geladenen Projekt: der ist nach der Handlung eines
 * Kollegen (z. B. letzter Bestandteil erledigt, eigener Klick bekam
 * `schon_erledigt`) veraltet und laesst „DB 1 offen" neben frischen Eintraegen
 * „0 offen" stehen — eine falsche Aufforderung zur Reparatur.
 */
export const bewerteFrischenStand = (a: {
    service: Pick<ServiceItem, 'id' | 'bestandteile'>;
    eintraege: readonly BestandteilEintrag[];
    frisch: Pick<PaketPositionFrisch, 'id' | 'bestandteile' | 'bestandteileOffen'> | null | undefined;
}): { stand: BestandteilStand; dbOffen: number | null | undefined; unlesbar: boolean } => {
    const stand = bestandteilStand(a.service, a.eintraege);
    const dbOffen = dbOffenAusFrisch(a.service, a.frisch);
    return { stand, dbOffen, unlesbar: dbOffen !== undefined && definitionUnlesbar(stand, dbOffen) };
};

export type PositionsStatusArt = 'kein' | 'offen' | 'unlesbar' | 'gebucht_automatisch' | 'gebucht' | 'nicht_gebucht';

export interface PositionsStatus {
    art: PositionsStatusArt;
    text: string;
    hinweis: string | null;
    /** Knopf „Position jetzt buchen" anbieten (Schreibrecht prueft der Aufrufer). */
    jetztBuchen: boolean;
    abgerechnet: boolean;
}

/** „M. Muster, Bestandteil SiGe-Plan" aus der BST-Zeile; Nachtlauf als solcher benannt. */
const ausloeserText = (t: ServiceTransaction): string => {
    const wer = String(t.bookedBy ?? '').trim();
    const was = /ausgelöst durch (.+)$/.exec(String(t.description ?? '').trim())?.[1]?.trim() || '';
    if (!wer || wer === BESTANDTEIL_SYSTEM_NAME) return 'im Nachtlauf';
    return `ausgelöst durch ${wer}${was ? `, ${was}` : ''}`;
};

/**
 * Status der POSITION im Kopf des Dialogs.
 *
 * Vollstaendig heisst hier: laut den geladenen Eintraegen ist jeder Bestandteil
 * erledigt oder entfallen. Ob die Position abrechenbar ist, entscheidet weiter
 * das Gate aus der Datenbank — der Knopf „Position jetzt buchen" prueft vor dem
 * Buchen deshalb den frischen DB-Stand (nie den hier gerechneten).
 *
 *  - offen: noch nicht abrechenbar; ist die Position trotzdem gebucht
 *    (Hand-Buchung, Bestandteil spaeter hinzugekommen), sagt der Hinweis das.
 *  - gebucht_automatisch: die BST-Zeile steht im Protokoll — mit Zeitpunkt und
 *    ausloesender Person (Plan, „Nachweis").
 *  - gebucht: voll gebucht ohne BST-Zeile (von Hand).
 *  - nicht_gebucht: vollstaendig, aber (noch) nicht gebucht — der Knopf.
 *  - unlesbar: die Datenbank zaehlt mehr offene als die App lesen kann
 *    (`dbOffen`, definitionUnlesbar) — gesperrt, NIE der Knopf.
 */
export const positionsStatus = (a: {
    service: Pick<ServiceItem, 'id' | 'quantityUsed' | 'quantityContracted' | 'quantityBilled'> & Partial<Pick<ServiceItem, 'bestandteile'>>;
    stand: BestandteilStand;
    serviceLog: readonly ServiceTransaction[] | null | undefined;
    /** Offene Bestandteile laut DATENBANK (bestandteileOffen bzw. RPC-Antwort). */
    dbOffen?: number | null;
}): PositionsStatus => {
    const abgerechnet = zahl(a.service?.quantityBilled) > TOLERANZ;
    // Unlesbar VOR „kein": eine Liste aus lauter unlesbaren Elementen hat
    // lokal gesamt 0, ist aber ein gesperrtes Paket.
    if (a.stand && definitionUnlesbar(a.stand, a.dbOffen)) {
        return {
            art: 'unlesbar',
            text: 'Nicht abrechenbar — die Datenbank zählt noch offene Bestandteile.',
            hinweis: UNLESBAR_HINWEIS,
            jetztBuchen: false,
            abgerechnet,
        };
    }
    if (!a.stand || a.stand.gesamt <= 0) {
        // `[]` (A3): ein Paket aus lauter unlesbaren Bestandteilen — nie „kein".
        // Offen > 0 steht oben als „unlesbar"; hier meldet die Datenbank 0
        // (frei, aber mit Deckel) oder keinen frischen Wert (unbekannt).
        if (hatBestandteile(a.service)) {
            const frei = a.dbOffen === 0;
            return {
                art: 'unlesbar',
                text: frei
                    ? 'Laut Datenbank vollständig — die Bestandteile sind hier nicht lesbar.'
                    : 'Nicht abrechenbar — der Stand der Bestandteile ist nicht lesbar.',
                hinweis: frei ? UNLESBAR_FREI_HINWEIS : UNLESBAR_HINWEIS,
                jetztBuchen: false,
                abgerechnet,
            };
        }
        return { art: 'kein', text: 'Diese Position hat keine Bestandteile.', hinweis: null, jetztBuchen: false, abgerechnet };
    }
    const bst = aktiveBstBuchung(a.serviceLog ?? [], String(a.service.id));
    const vertrag = zahl(a.service.quantityContracted);
    const vollGebucht = vertrag > TOLERANZ && zahl(a.service.quantityUsed) >= vertrag - TOLERANZ;

    if (!a.stand.vollstaendig) {
        return {
            art: 'offen',
            text: 'Noch nicht abrechenbar — es sind Bestandteile offen.',
            hinweis: bst || vollGebucht
                ? 'Die Position ist bereits gebucht, wird aber erst abrechenbar, wenn alle Bestandteile erledigt sind.'
                : null,
            jetztBuchen: false,
            abgerechnet,
        };
    }
    if (bst) {
        return {
            art: 'gebucht_automatisch',
            text: `Vollständig — Position gebucht am ${formatZeitpunkt(bst.bookedAt || bst.date)} (automatisch, ${ausloeserText(bst)}).`,
            hinweis: null,
            jetztBuchen: false,
            abgerechnet,
        };
    }
    if (vollGebucht) {
        return { art: 'gebucht', text: 'Vollständig — Position gebucht.', hinweis: null, jetztBuchen: false, abgerechnet };
    }
    return {
        art: 'nicht_gebucht',
        text: 'Vollständig, aber nicht gebucht.',
        hinweis: a.stand.erledigt === 0
            ? 'Alle Bestandteile entfallen — in diesem Fall bucht die Automatik nicht. Bitte bewusst entscheiden.'
            : 'Ohne Klick holt der Nachtlauf die Buchung nach.',
        jetztBuchen: true,
        abgerechnet,
    };
};

export const JETZT_BUCHEN_NUR_ADMIN = 'Alle Bestandteile entfallen — ein Admin muss die Buchung bestätigen.';

/**
 * Darf DIESE Person „Position jetzt buchen"? Sind alle Bestandteile nur
 * entfallen (keiner erledigt), ist die Buchung eine Entscheidung wie das
 * Entfallen selbst — Admin-Sache (E4). Alle anderen sehen den Hinweis.
 */
export const jetztBuchenFreigabe = (
    status: Pick<PositionsStatus, 'jetztBuchen'>,
    stand: Pick<BestandteilStand, 'erledigt'>,
    isAdmin: boolean,
): { knopf: boolean; hinweis: string | null } => {
    if (!status?.jetztBuchen) return { knopf: false, hinweis: null };
    if ((stand?.erledigt ?? 0) === 0 && !isAdmin) return { knopf: false, hinweis: JETZT_BUCHEN_NUR_ADMIN };
    return { knopf: true, hinweis: null };
};

/** „erledigt 16.10.2026, 14:32 Uhr von M. Muster" */
export const erledigtText = (e: Pick<BestandteilEintrag, 'erfasstAm' | 'erfasstVonName'>): string =>
    `erledigt ${formatZeitpunkt(e.erfasstAm)}${e.erfasstVonName ? ` von ${e.erfasstVonName}` : ''}`;

/**
 * „Leistungstag 15.10.2026" — nur, wenn er vom Erfassungstag (Berlin)
 * abweicht. Leistungstag und Erfassungszeitpunkt sind zwei Angaben (Plan,
 * „Nachweis"); gleich, waere die Zeile nur Laerm.
 */
export const leistungstagText = (e: Pick<BestandteilEintrag, 'erfasstAm' | 'leistungstag'>): string | null => {
    const erfasst = berlinTagVon(e.erfasstAm);
    if (!e.leistungstag || !erfasst || erfasst === e.leistungstag) return null;
    return `Leistungstag ${formatTag(e.leistungstag)}`;
};

const ANLASS_TEXT: Record<string, string> = { freigabe: 'Freigabe', versand: 'Versand' };
const ANLASS_VERB: Record<string, string> = { freigabe: 'freigegeben', versand: 'versendet' };

/**
 * Herkunft eines ERLEDIGT-Eintrags:
 *  - „durch Modul (Versand, Fassung 3)" — der Trigger hat erledigt;
 *  - „von Hand übernommen (Freigabe vom 12.10.2026, Fassung 2)" — Übernahme
 *    per Klick (RPC mit Ausgabe, `durch = 'hand'`);
 *  - „von Hand".
 * Fehlt die Ausgabe in der geladenen Liste (geloescht, nicht lesbar), steht
 * nur die Art da.
 */
export const herkunftText = (
    e: Pick<BestandteilEintrag, 'durch' | 'ausgabeId'>,
    ausgaben: readonly AusgabeLike[] | null | undefined,
): string => {
    const ausgabe = e.ausgabeId ? (ausgaben || []).find((a) => gleicheKennung(a.id, e.ausgabeId)) ?? null : null;
    const fassung = ausgabe?.fassungRef ? `, Fassung ${ausgabe.fassungRef}` : '';
    if (e.durch === 'modul') {
        return ausgabe ? `durch Modul (${ANLASS_TEXT[ausgabe.anlass] ?? ausgabe.anlass}${fassung})` : 'durch Modul';
    }
    if (e.ausgabeId) {
        if (!ausgabe) return 'von Hand übernommen aus dem Modul';
        const tag = berlinTagVon(ausgabe.erstelltAm);
        return `von Hand übernommen (${ANLASS_TEXT[ausgabe.anlass] ?? ausgabe.anlass}${tag ? ` vom ${formatTag(tag)}` : ''}${fassung})`;
    }
    return 'von Hand';
};

/** „entfällt — Grund" und „M. Muster, 16.10.2026, 14:32 Uhr". */
export const entfaelltText = (e: Pick<BestandteilEintrag, 'notiz' | 'erfasstAm' | 'erfasstVonName'>): { text: string; wer: string } => ({
    text: `entfällt${e.notiz ? ` — ${e.notiz}` : ''}`,
    wer: [e.erfasstVonName, formatZeitpunkt(e.erfasstAm)].filter(Boolean).join(', '),
});

/**
 * „Vorankündigung am 12.10.2026 versendet (A. Beispiel)" — die Ausgabe, die
 * uebernommen wird. Steht im Vorschlag und, seit die Uebernahme Stunden
 * verlangt (S2), auch im Formular, das „Übernehmen" oeffnet.
 */
export const uebernahmeInfo = (b: Pick<PositionsBestandteil, 'quelle'>, a: AusgabeLike): string => {
    const was = BESTANDTEIL_QUELLE_LABEL[b.quelle] ?? b.quelle;
    const tag = berlinTagVon(a.erstelltAm);
    const verb = ANLASS_VERB[a.anlass] ?? a.anlass;
    return `${was}${tag ? ` am ${formatTag(tag)}` : ''} ${verb}${a.erstelltVonName ? ` (${a.erstelltVonName})` : ''}`;
};

/** „Vorankündigung am 12.10.2026 versendet (A. Beispiel) — übernehmen?" */
export const uebernahmeText = (b: Pick<PositionsBestandteil, 'quelle'>, a: AusgabeLike): string =>
    `${uebernahmeInfo(b, a)} — übernehmen?`;

// ─────────────────────────────────────────────────────────────────────────────
// Eingaben
// ─────────────────────────────────────────────────────────────────────────────

/** Grund fuer „Entfällt" und „Zurücknehmen": mindestens 5 Zeichen (wie die DB). */
export const grundGueltig = (g: string | null | undefined): boolean => String(g ?? '').trim().length >= 5;

/** Leistungstag: ein Kalendertag, nie nach heute (Berlin). */
export const leistungstagGueltig = (tag: string | null | undefined, heute: string): boolean => {
    const t = String(tag ?? '').trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(t) && t <= heute;
};

// ─────────────────────────────────────────────────────────────────────────────
// Meldungen nach einer Handlung
// ─────────────────────────────────────────────────────────────────────────────

export type MeldungTon = 'erfolg' | 'hinweis' | 'fehler';

export interface Meldung {
    ton: MeldungTon;
    text: string;
    /** Neue Programmversion — die Meldung bietet „App neu laden" an. */
    neuLaden?: boolean;
}

/** Fehlschlag einer RPC → Meldung (Text aus RPC_STATUS_TEXT, bei `fehler` mit Detail). */
export const rpcMeldung = (rpc: BestandteilRpcErgebnis): Meldung => {
    const text = RPC_STATUS_TEXT[rpc.status] ?? RPC_STATUS_TEXT.fehler;
    const detail = rpc.status === 'fehler' && rpc.fehler ? ` (${rpc.fehler})` : '';
    const ton: MeldungTon = rpc.status === 'schon_erledigt' || rpc.status === 'schon_zurueckgenommen' ? 'hinweis' : 'fehler';
    return { ton, text: `${text}${detail}`, ...(rpc.status === 'build_veraltet' ? { neuLaden: true } : {}) };
};

/** Gruende aus bestandteilBuchung im Klartext. Unbekanntes bleibt roh stehen. */
const GRUND_TEXT: Record<string, string> = {
    konflikt: 'jemand hat das Projekt inzwischen gespeichert — bitte neu laden',
    build_veraltet: 'es gibt eine neue Programmversion — bitte die App neu laden',
    bauphase: 'die App wird gerade aktualisiert — bitte später erneut versuchen',
    entfernung_nicht_verfuegbar: 'die Datenbank kann die Buchung hier nicht entfernen',
    nicht_auf_dem_server: 'die Buchung ist auf dem Server nicht (mehr) zu finden — bitte neu laden',
    menge_ungueltig: 'die Position hat keine gültige Vertragsmenge',
    position_fehlt: 'die Position wurde nicht gefunden',
    kein_paket: 'die Position hat keine Bestandteile (mehr)',
    abgerechnet: 'die Position ist bereits abgerechnet',
    abrechnungsstand_unbekannt: 'der Abrechnungsstand der Position ist unbekannt — bitte an die Buchhaltung wenden',
    projekt_ohne_id: 'das Projekt ist noch nicht gespeichert',
};

export const grundText = (grund: string | null | undefined): string => {
    const roh = String(grund ?? '').trim();
    if (!roh) return 'unbekannter Grund';
    const kopf = roh.split(':')[0].trim();
    if (GRUND_TEXT[roh]) return GRUND_TEXT[roh];
    if (GRUND_TEXT[kopf]) {
        const rest = roh.slice(kopf.length + 1).trim();
        return rest ? `${GRUND_TEXT[kopf]}: ${rest}` : GRUND_TEXT[kopf];
    }
    return roh;
};

const istNeuLaden = (grund: string | null | undefined): boolean => String(grund ?? '').startsWith('build_veraltet');

type Folge = { status: AusfuehrungsStatus; grund?: string } | null | undefined;

/**
 * Meldung nach „Erledigt buchen", Übernahme oder „Entfällt" — der Bestandteil
 * selbst IST dann erledigt (die RPC hat 'erledigt'/'entfaellt' gemeldet); die
 * Meldung sagt, was mit der POSITION geschah.
 */
export const meldungNachErledigung = (a: {
    label: string;
    verb: 'erledigt' | 'entfällt';
    positionTitel: string;
    gesamt: number;
    offenDanach: number | null | undefined;
    folge: Folge;
}): Meldung => {
    const was = `„${a.label}“ ${a.verb}`;
    const titel = `„${a.positionTitel || 'Position'}“`;
    const f = a.folge;
    if (f?.status === 'gebucht') return { ton: 'erfolg', text: `${was}. Position ${titel} gebucht — jetzt abrechenbar.` };
    const offen = a.offenDanach;
    if (typeof offen === 'number' && offen > 0) {
        const bruch = a.gesamt > 0 && offen <= a.gesamt ? ` — Bestandteile ${a.gesamt - offen}/${a.gesamt}` : '';
        return { ton: 'erfolg', text: `${was}${bruch}.` };
    }
    if (!f || (f.status === 'nicht_noetig' && f.grund === 'nicht_vollstaendig')) return { ton: 'erfolg', text: `${was}.` };
    if (f.grund === 'nur_entfallen') {
        return {
            ton: 'hinweis',
            text: `${was}. Alle Bestandteile sind erledigt oder entfallen, aber keiner wurde erledigt — die Position wird deshalb nicht automatisch gebucht. Bei Bedarf „Position jetzt buchen“.`,
        };
    }
    if (f.grund === 'tochterprojekt') {
        return { ton: 'hinweis', text: `${was}. Die Position ist vollständig — gebucht wird im Mutterprojekt bzw. im Nachtlauf.` };
    }
    if (f.status === 'nicht_noetig' && f.grund === 'schon_gebucht') {
        return { ton: 'erfolg', text: `${was}. Die Position war bereits gebucht.` };
    }
    if (f.status === 'nicht_noetig') return { ton: 'erfolg', text: `${was}.` };
    return {
        ton: 'hinweis',
        text: `${was}, die Position ${titel} wurde aber nicht gebucht (${grundText(f.grund)}). Der Nachtlauf holt das nach.`,
        ...(istNeuLaden(f.grund) ? { neuLaden: true } : {}),
    };
};

/** Meldung nach „Zurücknehmen" (die RPC hat 'zurueckgenommen' gemeldet). */
export const meldungNachRuecknahme = (a: { label: string; positionTitel: string; folge: Folge }): Meldung => {
    const was = `Eintrag zu „${a.label}“ zurückgenommen — der Bestandteil ist wieder offen`;
    const f = a.folge;
    if (f?.status === 'zurueckgenommen') {
        return {
            ton: 'erfolg',
            text: `${was}. Die automatische Buchung der Position „${a.positionTitel}“ wurde mit zurückgenommen; sie ist wieder nicht abrechenbar.`,
        };
    }
    if (!f || f.status === 'nicht_noetig') return { ton: 'erfolg', text: `${was}.` };
    if (f.grund === 'abgerechnet' || String(f.grund ?? '').startsWith('abgerechnet')) {
        return { ton: 'hinweis', text: `${was}. ${ZURUECKNEHMEN_ABGERECHNET} Die Buchung der Position bleibt bis dahin stehen.` };
    }
    if (f.grund === 'tochterprojekt') {
        return { ton: 'hinweis', text: `${was}. Die Positionsbuchung liegt im Mutterprojekt und wird dort zurückgenommen.` };
    }
    return {
        ton: 'fehler',
        text: `${was}, die Buchung der Position aber NICHT (${grundText(f.grund)}). Die Position bleibt gebucht und ist nicht abrechenbar; der Projektcheck meldet den Widerspruch.`,
        ...(istNeuLaden(f.grund) ? { neuLaden: true } : {}),
    };
};

/** Meldung nach dem Speichern einer neuen Liste (nur Admin). */
export const meldungNachDefinition = (positionTitel: string, folge: Folge): Meldung => {
    const titel = `„${positionTitel || 'Position'}“`;
    if (folge?.status === 'gebucht') {
        return { ton: 'erfolg', text: `Bestandteile gespeichert. Die Position ${titel} ist damit vollständig und gebucht — jetzt abrechenbar.` };
    }
    if (!folge || folge.status === 'nicht_noetig') return { ton: 'erfolg', text: 'Bestandteile gespeichert.' };
    return {
        ton: 'hinweis',
        text: `Bestandteile gespeichert. Die Position ${titel} ist vollständig, wurde aber nicht gebucht (${grundText(folge.grund)}) — der Nachtlauf holt das nach.`,
        ...(istNeuLaden(folge.grund) ? { neuLaden: true } : {}),
    };
};

/** Meldung nach dem Knopf „Position jetzt buchen". */
export const meldungNachJetztBuchen = (positionTitel: string, r: Folge): Meldung => {
    const titel = `„${positionTitel || 'Position'}“`;
    if (r?.status === 'gebucht') return { ton: 'erfolg', text: `Position ${titel} gebucht — jetzt abrechenbar.` };
    if (r?.grund === 'schon_gebucht') return { ton: 'hinweis', text: `Position ${titel} war bereits gebucht.` };
    if (r?.grund === 'nicht_vollstaendig') {
        return { ton: 'hinweis', text: 'Laut Datenbank sind noch Bestandteile offen — es wurde nichts gebucht. Bitte neu laden.' };
    }
    if (r?.grund === 'tochterprojekt') return { ton: 'hinweis', text: 'Tochterprojekt: gebucht wird im Mutterprojekt.' };
    return {
        ton: 'fehler',
        text: `Position ${titel} wurde nicht gebucht (${grundText(r?.grund ?? r?.status)}).`,
        ...(istNeuLaden(r?.grund) ? { neuLaden: true } : {}),
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Stunden und Stundenkontingent (docs/stundenkontingent-vertrag.md, Paket D)
// ─────────────────────────────────────────────────────────────────────────────
//
// Rein intern (S8): nichts hier sperrt eine Buchung oder fasst eine Menge an.
// Die Regeln selbst (Ampel, Summe, Rechte) stehen in stundenLogic.ts; hier nur,
// was der Dialog daraus macht — Felder, Knoepfe, Texte, Meldungen.

/** Kennt die Datenbank die Stunden noch nicht (Nr. 171/172 fehlen), wird ohne sie erledigt. */
export const STUNDEN_NICHT_VERFUEGBAR =
    'Die Stundenerfassung ist in dieser Umgebung noch nicht eingerichtet — es wird ohne Stunden gebucht.';

/** Sichtbarer Hinweis unter einem Stundenfeld mit ungueltiger Eingabe (S9). */
export const STUNDEN_FORMAT_HINWEIS =
    'Bitte Stunden größer 0 bis 9999 angeben, höchstens zwei Nachkommastellen (z. B. 7,5).';

/** Wie oben, fuer einen „entfällt"-Eintrag — dort ist auch 0 erlaubt. */
export const STUNDEN_FORMAT_HINWEIS_ENTFAELLT =
    'Bitte 0 bis 9999 Stunden angeben, höchstens zwei Nachkommastellen (z. B. 7,5).';

/** Wie oben, fuer das Kontingent (S9: 0 < h ≤ 99.999). */
export const KONTINGENT_FORMAT_HINWEIS =
    'Bitte ein Kontingent größer 0 bis 99.999 Stunden angeben, höchstens zwei Nachkommastellen (z. B. 100 oder 62,5).';

/** Ergebnis einer Stundeneingabe: darf gebucht werden, und mit welchem Wert (fehlt: ohne Stunden). */
export interface StundenEingabe {
    ok: boolean;
    stunden?: number;
}

/**
 * „Erledigt buchen" und „Übernehmen" (S2): Stunden PFLICHT, 0 < h ≤ 9999.
 * Kennt die Datenbank die Stunden nicht (`stundenVerfuegbar` false), gibt es
 * das Feld nicht — dann geht es ohne Stunden, wie vor Nr. 171.
 */
export const erledigenStunden = (eingabe: string | null | undefined, stundenVerfuegbar: boolean): StundenEingabe => {
    if (!stundenVerfuegbar) return { ok: true };
    const h = parseStunden(String(eingabe ?? ''));
    return h === null ? { ok: false } : { ok: true, stunden: h };
};

/**
 * „Entfällt" (S2): Stunden freiwillig, 0 erlaubt. Leer → ohne Stunden. Steht
 * etwas da, muss es gueltig sein — eine Tippfehler-Eingabe still zu
 * verwerfen, hiesse „ohne Stunden" buchen, obwohl jemand welche angab.
 */
export const entfaelltStunden = (eingabe: string | null | undefined, stundenVerfuegbar: boolean): StundenEingabe => {
    const roh = String(eingabe ?? '').trim();
    if (!stundenVerfuegbar || !roh) return { ok: true };
    const h = parseStunden(roh, { nullStundenErlaubt: true });
    return h === null ? { ok: false } : { ok: true, stunden: h };
};

/**
 * Stunden nachtragen oder aendern (S7). Wert Pflicht — bei einem
 * „entfällt"-Eintrag ist 0 erlaubt (S9). Aendern verlangt einen Grund
 * (≥ 5 Zeichen); beim Nachtragen ist er freiwillig.
 */
export const stundenSetzenEingabe = (a: {
    eingabe: string | null | undefined;
    grund: string | null | undefined;
    art: BestandteilEintrag['art'];
    aendern: boolean;
}): { ok: boolean; stunden: number | null; wertOk: boolean; grundOk: boolean } => {
    const stunden = parseStunden(String(a?.eingabe ?? ''), { nullStundenErlaubt: a?.art === 'entfaellt' });
    const wertOk = stunden !== null;
    const grundOk = !a?.aendern || grundGueltig(a.grund);
    return { ok: wertOk && grundOk, stunden, wertOk, grundOk };
};

/** Kontingent (nur Admin): 0 < h ≤ 99.999, zwei Nachkommastellen; sonst null. */
export const kontingentAusEingabe = (eingabe: string | null | undefined): number | null =>
    parseStunden(String(eingabe ?? ''), { max: KONTINGENT_MAX });

/**
 * Vorbelegung eines Stundenfelds: 62.5 → '62,5', 100 → '100', null → ''.
 * Bewusst OHNE Tausenderpunkt (formatStundenZahl setzt ab 10.000 einen) —
 * „12.345,5" liest parseStunden zu Recht nicht.
 */
export const stundenVorbelegung = (h: number | null | undefined): string =>
    typeof h === 'number' && Number.isFinite(h) && h >= 0 ? String(Number(h.toFixed(2))).replace('.', ',') : '';

/** Stunden als Zahl ≥ 0, sonst null (fehlt, kaputt). */
const stundenOderNull = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;

/** Kontingent als Zahl, sonst null (fehlt, kaputt). Den Bereich wertet stundenLogic. */
const kontingentOderNull = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;

export interface ZeilenStunden {
    /** „8 h" — die Stunden des aktiven Eintrags; null: keine eingetragen. */
    anzeige: string | null;
    /** Gelb „Stunden fehlen": erledigt, aber ohne Stunden (S2/S3). „Entfällt" braucht keine. */
    fehlen: boolean;
    /** Knopf „Stunden eintragen" (S7: jeder mit Projektzugriff). */
    nachtragen: boolean;
    /** Knopf „Stunden ändern" (S7: wer den Eintrag erfasst hat, oder Admin — mit Grund). */
    aendern: boolean;
}

/**
 * Was eine Zeile zu den Stunden zeigt. Nur fuer erledigte und entfallene
 * Zeilen mit aktivem Eintrag, und nur, wenn die Datenbank die Stunden kennt —
 * sonst sind sie UNBEKANNT, nicht „fehlend" (kein gelber Hinweis, kein Knopf).
 *
 * Die Rechte kommen aus stundenRechte (Spiegel der RPC); die Kennungen gehen
 * klein geschrieben hinein — eine uuid ist in jeder Schreibweise dieselbe
 * (wie bei zeilenKnoepfe). Abgerechnet sperrt hier nichts (S8).
 */
export const zeilenStunden = (a: {
    zeile: BestandteilZeile;
    stundenVerfuegbar: boolean;
    schreibbar: boolean;
    isAdmin: boolean;
    userId: string | null;
}): ZeilenStunden => {
    const leer: ZeilenStunden = { anzeige: null, fehlen: false, nachtragen: false, aendern: false };
    const e = a?.zeile?.eintrag;
    if (!a.stundenVerfuegbar || !e || a.zeile.status === 'offen' || e.zurueckgenommenAm) return leer;
    const h = stundenOderNull(e.stunden);
    const klein = (v: string | null | undefined) => (v ? String(v).trim().toLowerCase() : null);
    const r = stundenRechte({
        eintrag: { stunden: h, erfasstVon: klein(e.erfasstVon), zurueckgenommenAm: e.zurueckgenommenAm },
        userId: klein(a.userId),
        isAdmin: a.isAdmin,
        schreibbar: a.schreibbar,
    });
    return {
        anzeige: h === null ? null : formatStunden(h),
        fehlen: h === null && a.zeile.status === 'erledigt',
        nachtragen: r.nachtragen,
        aendern: r.aendern,
    };
};

export type StundenTon = 'gruen' | 'rot' | 'grau';

export interface StundenKopf {
    /** „62,5 von 100 h · 37,5 h frei" bzw. „… · 12 h über dem Kontingent" (stundenText). */
    text: string;
    ton: StundenTon;
    /** Balkenbreite 0–100 (ueber dem Kontingent: 100, rot); null ohne Kontingent — dann kein Balken. */
    balken: number | null;
    /** Gelb „2 Bestandteile ohne Stunden"; null, wenn keiner fehlt. */
    ohneStunden: string | null;
}

/** „1 Bestandteil ohne Stunden" / „3 Bestandteile ohne Stunden"; null bei 0. */
export const ohneStundenText = (n: number): string | null => {
    const z = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
    if (z === 0) return null;
    return `${z} ${z === 1 ? 'Bestandteil' : 'Bestandteile'} ohne Stunden`;
};

/** Der Stundenblock im Kopf des Dialogs (S5): gruen bis 100 %, darueber rot, ohne Kontingent neutral. */
export const stundenKopf = (s: StundenStand): StundenKopf => {
    const ton: StundenTon = s.ampel === 'rot' ? 'rot' : s.ampel === 'gruen' ? 'gruen' : 'grau';
    const balken = s.anteil === null || !Number.isFinite(s.anteil) ? null : Math.max(0, Math.min(100, s.anteil * 100));
    return { text: stundenText(s), ton, balken, ohneStunden: ohneStundenText(s.ohneStunden) };
};

/**
 * Die frischen Stunden einer Position laut DATENBANK (ladePaketPositionenFrisch)
 * — nur, was sie kennt: ein Schluessel fehlt, wenn die Spalte nicht gelesen
 * wurde (Datenbank ohne Nr. 171).
 */
export interface StundenFrisch {
    /** `stunden_erfasst` — null: unbekannt. */
    erfasst?: number | null;
    /** `stunden_kontingent` — null: keins hinterlegt. */
    kontingent?: number | null;
}

/**
 * Die Stunden der frisch geladenen Position — nur zur SELBEN Position, und nur
 * die Felder, die die Ladung kennt. undefined: nichts bekannt.
 */
export const stundenAusFrisch = (
    service: Pick<ServiceItem, 'id'> | null | undefined,
    frisch: Pick<PaketPositionFrisch, 'id' | 'stundenErfasst' | 'stundenKontingent'> | null | undefined,
): StundenFrisch | undefined => {
    if (!frisch || !gleicheKennung(frisch.id, service?.id)) return undefined;
    const out: StundenFrisch = {};
    if (frisch.stundenKontingent !== undefined) out.kontingent = kontingentOderNull(frisch.stundenKontingent);
    if (frisch.stundenErfasst !== undefined) out.erfasst = stundenOderNull(frisch.stundenErfasst);
    return 'kontingent' in out || 'erfasst' in out ? out : undefined;
};

/** Gleiche frische Stunden? (Damit eine Ladung ohne Aenderung keinen neuen Stand erzeugt.) */
export const gleicheStundenFrisch = (a: StundenFrisch | undefined, b: StundenFrisch | undefined): boolean =>
    a === b || (!!a && !!b && a.erfasst === b.erfasst && a.kontingent === b.kontingent
        && ('erfasst' in a) === ('erfasst' in b) && ('kontingent' in a) === ('kontingent' in b));

/**
 * Das Kontingent fuer den Kopf: der frische DB-Wert, wenn bekannt — sonst der
 * Wert im geladenen Projekt. Ein Admin kann es inzwischen geaendert haben;
 * der Stand im Projekt folgt erst mit dem Neuladen.
 */
export const kontingentFuerKopf = (
    service: Pick<ServiceItem, 'stundenKontingent'> | null | undefined,
    frisch: StundenFrisch | undefined,
): number | null =>
    frisch && frisch.kontingent !== undefined ? frisch.kontingent : kontingentOderNull(service?.stundenKontingent);

/** Status, nach denen ein Stundenformular zu ist — es gibt dort nichts mehr zu tun. */
const STUNDEN_FORMULAR_ZU = new Set<StundenRpcErgebnis['status']>(['gesetzt', 'unveraendert', 'zurueckgenommen', 'eintrag_fehlt', 'position_fehlt', 'veraltet']);

/**
 * Stand beim NACHTRAGEN schon ein Wert da? Das Formular „Stunden eintragen"
 * hat kein Grundfeld — trug inzwischen jemand Stunden ein (Kollege, Pflicht-
 * fenster im Modul), antwortet die RPC mit `grund_zu_kurz` (eigener Eintrag
 * bzw. Admin: Aendern verlangt einen Grund) oder `nur_admin` (fremder
 * Eintrag). Beides ist dann kein Eingabefehler, sondern ein ueberholter Stand.
 */
export const stundenInzwischenEingetragen = (status: StundenRpcErgebnis['status'], aendern: boolean): boolean =>
    !aendern && (status === 'grund_zu_kurz' || status === 'nur_admin');

/**
 * Meldung nach „Stunden eintragen"/„Stunden ändern" (RPC `bestandteil_stunden_setzen`).
 *
 * `nachtragen` (optional): kam die Antwort auf ein NACHTRAGEN, und welche
 * Stunden stehen laut frischer Ladung jetzt da? Ist inzwischen ein Wert
 * eingetragen (stundenInzwischenEingetragen), geht das Formular zu — eine
 * Meldung „Grund angeben" ohne Grundfeld waere eine Sackgasse — und der
 * Hinweis sagt, wie es weitergeht.
 */
export const meldungNachStunden = (
    rpc: StundenRpcErgebnis,
    label: string,
    nachtragen?: { aendern: boolean; stundenJetzt?: number | null },
): { meldung: Meldung; schliessen: boolean } => {
    if (nachtragen && stundenInzwischenEingetragen(rpc.status, nachtragen.aendern)) {
        const h = stundenOderNull(nachtragen.stundenJetzt);
        const wert = h === null ? '' : ` (${formatStunden(h)})`;
        const weiter = rpc.status === 'nur_admin'
            ? 'ändern kann sie nur, wer den Eintrag erfasst hat, oder ein Admin.'
            : 'zum Ändern „Stunden ändern“ wählen.';
        return { meldung: { ton: 'hinweis', text: `Inzwischen hat jemand Stunden zu „${label}“ eingetragen${wert} — ${weiter}` }, schliessen: true };
    }
    const schliessen = STUNDEN_FORMULAR_ZU.has(rpc.status);
    if (rpc.status === 'gesetzt') {
        const h = stundenOderNull(rpc.stunden);
        return { meldung: { ton: 'erfolg', text: `Stunden zu „${label}“ gespeichert${h === null ? '' : `: ${formatStunden(h)}`}.` }, schliessen };
    }
    const text = STUNDEN_RPC_STATUS_TEXT[rpc.status] ?? STUNDEN_RPC_STATUS_TEXT.fehler;
    const detail = rpc.status === 'fehler' && rpc.fehler ? ` (${rpc.fehler})` : '';
    return {
        meldung: {
            ton: rpc.status === 'unveraendert' || rpc.status === 'zurueckgenommen' ? 'hinweis' : 'fehler',
            text: `${text}${detail}`,
            ...(rpc.status === 'build_veraltet' ? { neuLaden: true } : {}),
        },
        schliessen,
    };
};

/** Meldung nach „Kontingent speichern"/„Kontingent entfernen" (RPC `position_stundenkontingent_setzen`). */
export const meldungNachKontingent = (rpc: KontingentRpcErgebnis, gesendet: number | null): Meldung => {
    if (rpc.status === 'gesetzt') {
        const k = rpc.stundenKontingent !== undefined ? kontingentOderNull(rpc.stundenKontingent) : gesendet;
        return { ton: 'erfolg', text: k === null ? 'Stundenkontingent entfernt.' : `Stundenkontingent gespeichert: ${formatStunden(k)}.` };
    }
    const text = KONTINGENT_RPC_STATUS_TEXT[rpc.status] ?? KONTINGENT_RPC_STATUS_TEXT.fehler;
    const detail = rpc.status === 'fehler' && rpc.fehler ? ` (${rpc.fehler})` : '';
    return {
        ton: rpc.status === 'unveraendert' ? 'hinweis' : 'fehler',
        text: `${text}${detail}`,
        ...(rpc.status === 'build_veraltet' ? { neuLaden: true } : {}),
    };
};

/**
 * Die Felder fuer die Position im Projektstand nach `gesetzt` — aus der
 * ANTWORT der Datenbank (sie hat `stunden_erfasst` dabei neu gerechnet).
 * Nennt sie das Kontingent nicht, gilt der gesendete Wert; die Summe nur,
 * wenn sie genannt ist.
 */
export const kontingentFelderAusAntwort = (
    rpc: Pick<KontingentRpcErgebnis, 'stundenKontingent' | 'stundenErfasst'>,
    gesendet: number | null,
): Pick<ServiceItem, 'stundenKontingent' | 'stundenErfasst'> => ({
    stundenKontingent: rpc.stundenKontingent !== undefined ? kontingentOderNull(rpc.stundenKontingent) : gesendet,
    ...(rpc.stundenErfasst !== undefined ? { stundenErfasst: stundenOderNull(rpc.stundenErfasst) } : {}),
});

/**
 * REIN. Der Projektstand mit den neuen Stundenfeldern einer Position — EIN
 * onUpdate (Muster planeDefinitionSpeichern). Per Spread, alles andere der
 * Position (auch `bestandteileBasis`) bleibt. null, wenn die Position nicht in
 * diesem Stand steht (dann wird nichts geschrieben).
 *
 * Gesendet wird beim Speichern nur `stunden_kontingent`, und bei einer
 * bestehenden Position haelt die Datenbank ohnehin ihren Wert (S10) — der hier
 * gesetzte ist derselbe, den die RPC eben geschrieben hat. `stundenErfasst`
 * geht nie mit (NUR LESEN).
 */
export const berichtMitStunden = (
    bericht: Report,
    serviceId: string,
    felder: Pick<ServiceItem, 'stundenKontingent' | 'stundenErfasst'>,
): Report | null => {
    const gd = bericht?.metadata?.globalData;
    if (!gd) return null;
    const services = gd.services || [];
    const index = services.findIndex((s) => s && gleicheKennung(s.id, serviceId));
    if (index === -1) return null;
    const neu: ServiceItem = { ...services[index] };
    if (felder.stundenKontingent !== undefined) neu.stundenKontingent = felder.stundenKontingent;
    if (felder.stundenErfasst !== undefined) neu.stundenErfasst = felder.stundenErfasst;
    const neueServices = [...services];
    neueServices[index] = neu;
    return { ...bericht, metadata: { ...bericht.metadata, globalData: { ...gd, services: neueServices } } };
};

// ─────────────────────────────────────────────────────────────────────────────
// Lokale Ueberschreibung des Stands (Plakette im Hub)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * serviceId → offen nach der letzten Handlung bzw. dem letzten frischen Laden,
 * dazu der `bestandteileOffen`-Wert, den die Position im Client-Stand DAMALS
 * hatte (`basis`).
 *
 * Warum: die Plakette im Hub liest `service.bestandteileOffen` aus dem
 * geladenen Projekt. Nach einem Klick im Dialog weiss die Datenbank es besser
 * — fuer die Anzeige allein zu speichern hiesse aber, einen ganzen
 * Speichervorgang auszuloesen. Die Ueberschreibung lebt deshalb nur in der
 * Sitzung und gilt nur, solange der Client-Stand sich seither NICHT bewegt
 * hat: wurde neu geladen oder hat eine Positionsbuchung den Wert im Stand
 * gesetzt, gewinnt wieder der Stand.
 *
 * STUNDEN (Stundenkontingent, Paket D): dasselbe fuer `stundenErfasst` und
 * `stundenKontingent`. Erledigen und „Entfällt" melden die neue Summe nicht
 * zurueck — ohne Ueberschreibung zeigte die Stunden-Plakette im Hub bis zum
 * Neuladen des Projekts den alten Stand. Der Teil `stunden` traegt die frischen
 * DB-Werte und den Client-Stand DAMALS (`basisErfasst`/`basisKontingent`); er
 * gilt unabhaengig vom Teil `offen` — eine Positionsbuchung, die
 * `bestandteileOffen` im Stand setzt, macht die frische Stundensumme nicht alt.
 */
export interface StundenUeberschreibung {
    /** Frische Summe — fehlt: keine abweichende bekannt. */
    erfasst?: number;
    /** Frisches Kontingent (null = keins) — fehlt: kein abweichendes bekannt. */
    kontingent?: number | null;
    /** `stundenErfasst` im Client-Stand damals (fehlt → null). */
    basisErfasst: number | null;
    /** `stundenKontingent` im Client-Stand damals (fehlt → null). */
    basisKontingent: number | null;
}

export interface UeberschreibungEintrag {
    /** Frischer DB-Wert „offen" — fehlt: zu `offen` nichts Abweichendes bekannt. */
    offen?: number;
    /** `bestandteileOffen` im Client-Stand damals (nur mit `offen`). */
    basis?: number | null;
    unlesbar?: true;
    stunden?: StundenUeberschreibung;
}

export type OffenUeberschreibung = Readonly<Record<string, UeberschreibungEintrag>>;

const offenVon = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;

/**
 * Der Stunden-Teil aus frischen Werten — nur Bekanntes, und nur, was vom
 * Client-Stand abweicht. `erfasst` null heisst „unbekannt" und ueberschreibt
 * nie; `kontingent` null heisst „keins" und ist ein bekannter Wert.
 */
const stundenTeilAus = (
    service: Pick<ServiceItem, 'stundenErfasst' | 'stundenKontingent'> | null | undefined,
    frisch: StundenFrisch | null,
): StundenUeberschreibung | null => {
    if (!frisch) return null;
    const basisErfasst = stundenOderNull(service?.stundenErfasst);
    const basisKontingent = kontingentOderNull(service?.stundenKontingent);
    const erfasst = stundenOderNull(frisch.erfasst);
    const teil: Omit<StundenUeberschreibung, 'basisErfasst' | 'basisKontingent'> = {};
    if (erfasst !== null && erfasst !== basisErfasst) teil.erfasst = erfasst;
    if (frisch.kontingent !== undefined) {
        const k = kontingentOderNull(frisch.kontingent);
        if (k !== basisKontingent) teil.kontingent = k;
    }
    if (teil.erfasst === undefined && teil.kontingent === undefined) return null;
    return { ...teil, basisErfasst, basisKontingent };
};

const gleicherEintrag = (a: UeberschreibungEintrag | undefined, b: UeberschreibungEintrag): boolean =>
    !!a && a.offen === b.offen && (a.basis ?? null) === (b.basis ?? null) && !!a.unlesbar === !!b.unlesbar
    && JSON.stringify(a.stunden ?? null) === JSON.stringify(b.stunden ?? null);

/**
 * Neue Ueberschreibung setzen. Unveraendert → DIESELBE Referenz (sonst loeste
 * jedes Laden einen Render aus). Unbekannt (null) oder gleich dem Client-Stand
 * → Eintrag weg (fail closed: dann gilt, was im Projekt steht) — ausser die
 * Liste ist laut Dialog `unlesbar` (definitionUnlesbar): dieser Befund steht
 * nicht im Projekt und bleibt deshalb auch bei gleichem Wert stehen.
 *
 * `offen` undefined: zu „offen" nichts Frisches — der bisherige Teil bleibt.
 * `stunden` (optional, rueckwaertskompatibel): undefined → der bisherige
 * Stunden-Teil bleibt; null → er faellt weg (unbekannt); sonst gelten die
 * Regeln von `offen` (nur Bekanntes, nur Abweichendes, nur dieselbe Position).
 */
export const setzeOffenUeberschreibung = (
    vorher: OffenUeberschreibung,
    service: Pick<ServiceItem, 'id' | 'bestandteileOffen'> & Partial<Pick<ServiceItem, 'stundenErfasst' | 'stundenKontingent'>> | null | undefined,
    offen: number | null | undefined,
    unlesbar = false,
    stunden?: StundenFrisch | null,
): OffenUeberschreibung => {
    const id = String(service?.id ?? '');
    if (!id) return vorher;
    const alt = vorher[id];

    let offenTeil: Pick<UeberschreibungEintrag, 'offen' | 'basis' | 'unlesbar'> | null;
    if (offen === undefined) {
        offenTeil = alt && alt.offen !== undefined
            ? { offen: alt.offen, basis: alt.basis ?? null, ...(alt.unlesbar ? { unlesbar: true as const } : {}) }
            : null;
    } else {
        const wert = offenVon(offen);
        const basis = offenVon(service?.bestandteileOffen);
        offenTeil = wert === null || (wert === basis && !unlesbar)
            ? null
            : { offen: wert, basis, ...(unlesbar ? { unlesbar: true as const } : {}) };
    }
    const stundenTeil = stunden === undefined ? alt?.stunden ?? null : stundenTeilAus(service, stunden);

    if (!offenTeil && !stundenTeil) {
        if (!(id in vorher)) return vorher;
        const rest = { ...vorher };
        delete rest[id];
        return rest;
    }
    const neu: UeberschreibungEintrag = { ...(offenTeil ?? {}), ...(stundenTeil ? { stunden: stundenTeil } : {}) };
    if (gleicherEintrag(alt, neu)) return vorher;
    return { ...vorher, [id]: neu };
};

/**
 * Die Ueberschreibung dieser Position, soweit sie noch gilt — je Teil fuer
 * sich: `offen` solange `bestandteileOffen` im Stand unbewegt ist, `stunden`
 * solange `stundenErfasst` und `stundenKontingent` es sind.
 */
const geltendeUeberschreibung = (
    service: Pick<ServiceItem, 'id' | 'bestandteile' | 'bestandteileOffen'> & Partial<Pick<ServiceItem, 'stundenErfasst' | 'stundenKontingent'>> | null | undefined,
    ueberschreibung: OffenUeberschreibung | null | undefined,
): { offen?: number; unlesbar?: boolean; stunden?: StundenUeberschreibung } | null => {
    const e = service ? ueberschreibung?.[String(service.id)] : undefined;
    if (!e || !service || !hatBestandteile(service)) return null;
    const offenGilt = e.offen !== undefined && offenVon(service.bestandteileOffen) === (e.basis ?? null);
    const st = e.stunden;
    const stundenGilt = !!st
        && stundenOderNull(service.stundenErfasst) === st.basisErfasst
        && kontingentOderNull(service.stundenKontingent) === st.basisKontingent;
    if (!offenGilt && !stundenGilt) return null;
    return {
        ...(offenGilt ? { offen: e.offen as number, unlesbar: e.unlesbar === true } : {}),
        ...(stundenGilt ? { stunden: st } : {}),
    };
};

/** Die Position mit ueberschriebenem `bestandteileOffen` (und Stunden) — NUR fuer die Anzeige. */
export const mitUeberschreibung = <
    T extends Pick<ServiceItem, 'id' | 'bestandteile' | 'bestandteileOffen'> & Partial<Pick<ServiceItem, 'stundenErfasst' | 'stundenKontingent'>>,
>(
    service: T,
    ueberschreibung: OffenUeberschreibung | null | undefined,
): T => {
    const g = geltendeUeberschreibung(service, ueberschreibung);
    if (!g) return service;
    const st = g.stunden;
    return {
        ...service,
        ...(g.offen !== undefined ? { bestandteileOffen: g.offen } : {}),
        ...(st && st.erfasst !== undefined ? { stundenErfasst: st.erfasst } : {}),
        ...(st && st.kontingent !== undefined ? { stundenKontingent: st.kontingent } : {}),
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Plakette (Schnellzugriff, Details)
// ─────────────────────────────────────────────────────────────────────────────

/** 'rot' nur fuer die Stunden-Plakette (ueber dem Kontingent, S5). */
export type PlakettenTon = 'gelb' | 'gruen' | 'grau' | 'rot';

export interface PaketPlakette {
    text: string;
    ton: PlakettenTon;
    titel: string;
}

/**
 * „Bestandteile 4/5" (gelb), „Bestandteile 5/5" (gruen), Stand unbekannt
 * (grau). Allein aus Definition und `bestandteileOffen` (projectLv.
 * bestandteilStandKurz) — die Liste im Hub laedt keine Eintraege.
 *
 * `unlesbar` (vom Dialog ueber die Ueberschreibung gemeldet, definitionUnlesbar):
 * „Bestandteile ?" grau — die Zahl aus der lesbaren Liste waere hier eine
 * Behauptung, die das Gate nicht teilt.
 */
export const paketPlakette = (
    service: Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen'> | null | undefined,
    unlesbar = false,
): PaketPlakette | null => {
    if (!service || !hatBestandteile(service)) return null;
    // Paket ohne eine einzige LESBARE Zeile (A3: `[]`): unlesbar, auch ohne
    // Befund aus dem Dialog. Meldet die Datenbank 0 offen, ist es frei —
    // abrechenbar hoechstens bis zur Vertragsmenge (Deckel), der Titel sagt
    // das statt „bleibt gesperrt".
    if (unlesbar || !normalisiereBestandteile(service.bestandteile)) {
        const frei = !unlesbar && bestandteilGate(service) === 'frei';
        return { text: 'Bestandteile ?', ton: 'grau', titel: frei ? UNLESBAR_FREI_HINWEIS : UNLESBAR_HINWEIS };
    }
    const kurz = bestandteilStandKurz(service);
    if (!kurz) {
        return {
            text: 'Bestandteile: Stand unbekannt',
            ton: 'grau',
            titel: 'Der Stand der Bestandteile ist nicht ladbar — die Position gilt bis dahin als nicht abrechenbar.',
        };
    }
    if (bestandteilGate(service) === 'frei') {
        return { text: `Bestandteile ${kurz}`, ton: 'gruen', titel: 'Alle Bestandteile erledigt oder entfallen.' };
    }
    return { text: `Bestandteile ${kurz}`, ton: 'gelb', titel: 'Noch nicht abrechenbar — es sind Bestandteile offen.' };
};

/** Plakette mit der Ueberschreibung des Hubs (frischer Wert, Befund `unlesbar`). */
export const plaketteFuer = (
    service: Pick<ServiceItem, 'id' | 'bestandteile' | 'bestandteileOffen'> | null | undefined,
    ueberschreibung: OffenUeberschreibung | null | undefined,
): PaketPlakette | null => {
    if (!service) return null;
    const e = geltendeUeberschreibung(service, ueberschreibung);
    return paketPlakette(e?.offen !== undefined ? { ...service, bestandteileOffen: e.offen } : service, e?.unlesbar === true);
};

/**
 * Stunden-Plakette neben „Bestandteile 4/5": „62,5/100 h" (gruen), ueber dem
 * Kontingent rot (S5), ohne Kontingent „12,5 h" grau. Aus den DB-Feldern der
 * Position (stundenStandAusPosition), mit der Ueberschreibung des Hubs (frische
 * Summe nach einer Handlung im Dialog).
 *
 * null — die Plakette fehlt: kein Paket, nichts bekannt, oder weder
 * Kontingent noch erfasste Stunden (kein Kontingent UND 0 h). Rein intern
 * (S8), deshalb ohne Rollensperre: Stunden sind keine Preise.
 */
export const stundenPlaketteFuer = (
    service: Pick<ServiceItem, 'id' | 'bestandteile' | 'bestandteileOffen'> & Partial<Pick<ServiceItem, 'stundenErfasst' | 'stundenKontingent'>> | null | undefined,
    ueberschreibung: OffenUeberschreibung | null | undefined,
): PaketPlakette | null => {
    if (!service) return null;
    const s = stundenStandAusPosition(mitUeberschreibung(service, ueberschreibung));
    if (!s || (s.kontingent === null && s.erfasst <= 0)) return null;
    const p = stundenPlakette(s);
    if (!p.text) return null;
    return { text: p.text, ton: p.ton, titel: `Stunden (intern): ${stundenText(s)}` };
};

export const PLAKETTEN_KLASSE: Record<PlakettenTon, string> = {
    gelb: 'bg-amber-50 text-amber-800 border-amber-200',
    gruen: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    grau: 'bg-slate-100 text-slate-600 border-slate-200',
    rot: 'bg-red-50 text-red-700 border-red-200',
};

// ─────────────────────────────────────────────────────────────────────────────
// Navigation zum Modul
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Bestandteil-Quelle → Modulschluessel der Hub-Navigation (App.
 * executeModuleNavigation). Das Handbuch ist ein Reiter der SiGe-Plan-Huelle;
 * der Schluessel 'handbuch' oeffnet sie an diesem Reiter. 'hand' hat kein Modul.
 */
const MODUL_JE_QUELLE: Record<BestandteilQuelle, string | null> = {
    vorankuendigung: 'vorank',
    sigeplan: 'sigeplan',
    asplan: 'asplan',
    unterlage: 'unterlage',
    baustellenordnung: 'bauordnung',
    handbuch: 'handbuch',
    hand: null,
};

export const modulFuerQuelle = (q: BestandteilQuelle): string | null =>
    (Object.prototype.hasOwnProperty.call(MODUL_JE_QUELLE, q) ? MODUL_JE_QUELLE[q] : null) ?? null;

// ─────────────────────────────────────────────────────────────────────────────
// Festlegen (nur Admin): Keys, Vorlage, Speichern
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Alle je benutzten Keys der Position — aktuelle Definition, Keys aller
 * Eintraege (auch zurueckgenommener) und beide Seiten jeder Aenderung. Ein
 * neuer Bestandteil darf keinen davon bekommen, sonst erbte er verwaiste
 * Eintraege still (neuerBestandteilKey).
 */
export const belegteKeys = (
    service: Pick<ServiceItem, 'id' | 'bestandteile'>,
    eintraege: readonly BestandteilEintrag[],
    aenderungen: readonly BestandteilAenderung[],
): string[] => {
    const keys = new Set<string>();
    (normalisiereBestandteile(service?.bestandteile) ?? []).forEach((b) => keys.add(b.key));
    for (const e of eintraege || []) if (e && gleicheKennung(e.serviceId, service?.id)) keys.add(e.bestandteilKey);
    for (const a of aenderungen || []) {
        if (!a || !gleicheKennung(a.serviceId, service?.id)) continue;
        [...(a.vorher || []), ...(a.nachher || [])].forEach((b) => keys.add(b.key));
    }
    return [...keys];
};

/** Keys mit AKTIVEM Eintrag — Entfernen nur mit Rueckfrage im Editor. */
export const keysMitAktivemEintrag = (
    service: Pick<ServiceItem, 'id'>,
    eintraege: readonly BestandteilEintrag[],
): string[] => {
    const keys = new Set<string>();
    for (const e of eintraege || []) {
        if (e && !e.zurueckgenommenAm && gleicheKennung(e.serviceId, service?.id)) keys.add(e.bestandteilKey);
    }
    return [...keys];
};

/**
 * Die Katalogvorlage einer Position: die `service_catalog`-Zeile mit GLEICHER
 * LV-Nummer UND gleichem Titel (eine LV-Nummer allein ist zwischen Katalogen
 * nicht eindeutig). Fehlt sie oder hat sie keine Bestandteile → null, der
 * Editor zeigt dann keinen Vorlagen-Knopf.
 */
export const findeKatalogVorlage = (
    zeilen: ReadonlyArray<Record<string, unknown>> | null | undefined,
    service: Pick<ServiceItem, 'lvPosition' | 'title'>,
): PositionsBestandteil[] | null => {
    const lv = String(service?.lvPosition ?? '').trim();
    const titel = String(service?.title ?? '').trim();
    if (!lv || !titel) return null;
    for (const z of zeilen || []) {
        if (!z || String(z.lv_position ?? '').trim() !== lv || String(z.title ?? '').trim() !== titel) continue;
        const vorlage = normalisiereBestandteile(z.bestandteile);
        if (vorlage) return vorlage;
    }
    return null;
};

export const NEP_PAKET_TEXT = 'Eventualposition (NEP) kann kein Paket sein.';

/**
 * Warum eine Position KEINE Bestandteile bekommen kann — null, wenn sie es
 * kann. Eventualposition (NEP, nicht beauftragt) zuerst: ihre Menge zaehlt
 * erst mit der Buchung, ein Gate „alle Bestandteile erledigt" haette dort
 * nichts zu sperren. Dann die Regeln aus pruefeDefinition (Menge 1, keine
 * Pauschale, nicht gedeckt).
 */
export const festlegenSperre = (service: ServiceItem | null | undefined): string | null => {
    if (!service) return 'Position fehlt.';
    if (isNepService(service)) return NEP_PAKET_TEXT;
    const p = pruefeDefinition(service, [{ key: 'probe', label: 'Probe', quelle: 'hand' }]);
    return p.ok === false ? p.text : null;
};

/** Kann die Position ueberhaupt Bestandteile bekommen (kein NEP, Menge 1, keine Pauschale, nicht gedeckt)? */
export const kannBestandteileBekommen = (service: ServiceItem | null | undefined): boolean => festlegenSperre(service) === null;

/** Warnungen ueber dem Editor (Plan B4): gebucht oder abgerechnet. */
export const editorWarnungen = (
    service: Pick<ServiceItem, 'id' | 'quantityUsed' | 'quantityContracted' | 'quantityBilled'>,
    serviceLog: readonly ServiceTransaction[] | null | undefined,
    unlesbar = false,
): string[] => {
    const out: string[] = [];
    if (unlesbar) {
        out.push('Die gespeicherte Liste enthält Einträge, die diese App-Version nicht lesen kann. Speichern schreibt die hier sichtbare Liste neu — die unlesbaren Einträge fallen dabei weg. Gebucht wird dabei nicht.');
    }
    if (zahl(service?.quantityBilled) > TOLERANZ) {
        out.push('Die Position ist bereits abgerechnet. Eine Änderung der Bestandteile ändert an der Rechnung nichts mehr.');
        return out;
    }
    const vertrag = zahl(service?.quantityContracted);
    const gebucht = !!aktiveBstBuchung(serviceLog ?? [], String(service?.id ?? ''))
        || (vertrag > TOLERANZ && zahl(service?.quantityUsed) >= vertrag - TOLERANZ);
    if (gebucht) {
        out.push('Die Position ist bereits gebucht. Kommt ein offener Bestandteil hinzu, ist sie wieder nicht abrechenbar — die Buchung bleibt stehen.');
    }
    return out;
};

/** Meldung nach dem Neuspeichern einer unlesbaren Liste (Reparatur, ohne Buchung). */
export const REPARATUR_MELDUNG =
    'Bestandteile neu gespeichert — die nicht lesbaren Einträge fallen damit weg. Gebucht wird erst, wenn die Datenbank den neuen Stand bestätigt: nach dem Neuladen über „Position jetzt buchen“ oder im Nachtlauf.';

/** Ausloeser-Text einer Buchung, die durch das Speichern der Liste entsteht. */
export const AUSLOESER_LISTE = 'Änderung der Bestandteile';
/** Ausloeser-Text des Knopfs „Position jetzt buchen". */
export const AUSLOESER_KNOPF = 'Knopf „Position jetzt buchen“';

export type DefinitionsPlan =
    | { ok: false; text: string }
    | {
        ok: true;
        unveraendert: boolean;
        /** Der Stand mit der neuen Definition — EIN onUpdate (mit oder ohne Buchung). */
        bericht: Report;
        /** Offene Bestandteile mit der neuen Definition laut geladenen Eintraegen; null ohne Bestandteile. */
        offen: number | null;
        /** Gesetzt, wenn die neue Liste vollstaendig ist und mindestens einer erledigt — dann buchen. */
        buchen: { frisch: PaketPositionFrisch; leistungstage: string[]; bestandteilLabel: string } | null;
    };

/**
 * REIN. Neue Definition einer Position speichern (nur Admin, Plan B4).
 *
 *  - pruefeDefinition zuerst (E5: Menge 1, keine Pauschale) — Fehler als Text.
 *  - Der neue Stand traegt `bestandteile` (leer → null: „kein Paket mehr") und
 *    fuer die Anzeige `bestandteileOffen` aus den geladenen Eintraegen. Gesendet
 *    wird `bestandteileOffen` nie (cloudService nimmt es heraus), die Datenbank
 *    rechnet beim Speichern selbst (Trigger Nr. 166).
 *  - Macht die neue Liste die Position vollstaendig (jeder Bestandteil erledigt
 *    oder entfallen, mindestens einer erledigt), bucht DERSELBE Vorgang die
 *    Position (Kernregel 7, Plan B4: „Macht das Entfernen die Liste
 *    vollständig …"). Der Stand dafuer kommt aus dem lokalen Stand + den
 *    geladenen Eintraegen: die Datenbank kennt die neue Definition erst nach
 *    dem Speichern. Alle Riegel der Buchung (BST-Schluessel, schon gebucht,
 *    Menge) laufen in bucheVollstaendigePosition weiter.
 *  - Die Position wird per Spread geaendert: `bestandteileBasis` (A3, der
 *    geladene Stand, gegen den saveProjectToCloud entscheidet, ob die
 *    Definition mitgeht) bleibt erhalten.
 *
 * `reparatur` (die Datenbank zaehlt mehr offene, als die App lesen kann —
 * definitionUnlesbar): gespeichert wird die hier lesbare Liste, aber
 *  - NIE mit Buchung im selben Vorgang (ob die Position danach frei ist, weiss
 *    erst die Datenbank; „Kein Buchen in diesem Zustand");
 *  - und auch bei UNVERAENDERTER lesbarer Liste: dann ohne `bestandteileBasis`.
 *    Die Basis traegt die normalisierte Liste — gegen sie waere die
 *    unveraenderte Liste „nichts Neues", und saveProjectToCloud schickte sie
 *    nie; die unlesbaren Elemente blieben in der Spalte. Ohne Basis gilt die
 *    Definition als neu und geht mit (ServiceItem.bestandteileBasis). Bewusst
 *    nur in genau diesem Fall: ein Admin hat eben den frischen Stand vor sich
 *    und sagt ausdruecklich „neu speichern".
 */
export const planeDefinitionSpeichern = (a: {
    bericht: Report;
    serviceId: string;
    neu: PositionsBestandteil[] | null | undefined;
    eintraege: readonly BestandteilEintrag[];
    reparatur?: boolean;
}): DefinitionsPlan => {
    const gd = a.bericht?.metadata?.globalData;
    if (!gd) return { ok: false, text: 'Der Projektstand fehlt — bitte neu laden.' };
    const services = gd.services || [];
    const index = services.findIndex((s) => s && gleicheKennung(s.id, a.serviceId));
    if (index === -1) return { ok: false, text: 'Die Position steht nicht in diesem Projekt (Tochterprojekt?). Bitte im Projekt der Position ändern.' };
    const service = services[index];
    const roh = Array.isArray(a.neu) ? a.neu : [];
    // NEP zuerst (pruefeDefinition kennt die Regel erst mit Paket C); Entfernen geht immer.
    if (roh.length > 0 && isNepService(service)) return { ok: false, text: NEP_PAKET_TEXT };
    const pruefung = pruefeDefinition(service, roh);
    if (pruefung.ok === false) return { ok: false, text: pruefung.text };

    const neu = normalisiereBestandteile(roh);
    if (roh.length > 0 && (!neu || neu.length !== roh.length)) {
        return { ok: false, text: 'Mindestens ein Bestandteil ist unvollständig (Bezeichnung, Kennung oder Quelle fehlt).' };
    }
    const alt = normalisiereBestandteile(service.bestandteile);
    const gleich = JSON.stringify(alt ?? null) === JSON.stringify(neu ?? null);
    const reparatur = a.reparatur === true;
    // Reparatur speichert auch die unveraenderte lesbare Liste (siehe oben).
    const unveraendert = gleich && !reparatur;
    // Nur fuer die erzwungene Reparatur ohne Basis — sonst bleibt sie per Spread.
    const ohneBasis = (s: ServiceItem): ServiceItem => {
        if (!(reparatur && gleich)) return s;
        const { bestandteileBasis: _basis, ...rest } = s;
        return rest;
    };

    let neuerService: ServiceItem;
    let offen: number | null = null;
    let buchen: { frisch: PaketPositionFrisch; leistungstage: string[]; bestandteilLabel: string } | null = null;
    if (neu) {
        const stand = bestandteilStand({ id: service.id, bestandteile: neu }, a.eintraege);
        offen = stand.offen;
        neuerService = ohneBasis({ ...service, bestandteile: neu, bestandteileOffen: stand.offen });
        if (!reparatur && stand.vollstaendig && stand.erledigt >= 1) {
            const leistung = leistungAusEintraegen({ id: service.id, bestandteile: neu }, a.eintraege);
            buchen = {
                frisch: {
                    id: service.id,
                    title: service.title || '',
                    lvPosition: service.lvPosition || '',
                    bestandteile: neu,
                    bestandteileOffen: 0,
                    quantityUsed: zahl(service.quantityUsed),
                    quantityContracted: zahl(service.quantityContracted),
                    quantityBilled: zahl(service.quantityBilled),
                },
                leistungstage: leistung.leistungstage,
                bestandteilLabel: AUSLOESER_LISTE,
            };
        }
    } else {
        // Kein Paket mehr: `null` AUSDRUECKLICH, damit der Speicherweg die
        // Spalte leert (undefined hiesse „nicht mitsenden"). Der Anzeigewert
        // faellt weg — eine Position ohne Bestandteile hat kein Gate.
        const { bestandteileOffen: _weg, ...rest } = service;
        neuerService = ohneBasis({ ...rest, bestandteile: null });
    }
    const neueServices = [...services];
    neueServices[index] = neuerService;
    return {
        ok: true,
        unveraendert,
        bericht: { ...a.bericht, metadata: { ...a.bericht.metadata, globalData: { ...gd, services: neueServices } } },
        offen,
        buchen,
    };
};
